import { useCallback, useEffect, useRef, useState } from 'react'
import { useGameStore } from '@/store/gameStore'
import { useSessionStore } from '@/store/sessionStore'
import { useAudioContext } from '@/contexts/AudioContext'
import { GAME_CONFIG } from '@/types'
import { fromMinor, toMinor, MINOR_PER_MAJOR } from '@/utils/money'

const BET_STEP_MINOR = MINOR_PER_MAJOR / 10

function parseBetInput(value: string): number {
  const amount = Number(value.replace(',', '.'))
  return Number.isFinite(amount) ? toMinor(amount) : 0
}

// Стиль шрифта для калькулятора (Big Shoulders Display)
const calcFontStyle: React.CSSProperties = {
  fontFamily: "'Big Shoulders Display', sans-serif",
  fontWeight: 800,
  fontSize: '24px',
  lineHeight: '29px',
  textAlign: 'center',
  color: '#243043',
}

// Стиль для кнопки BET IN (Caesar Dressing)
const betInFontStyle: React.CSSProperties = {
  fontFamily: "'Caesar Dressing', cursive",
  fontWeight: 400,
  fontSize: '46px',
  lineHeight: '91.8%',
  textAlign: 'center',
  color: '#F8DDCA',
}

// Общий стиль красной кнопки (BET IN / Drop)
const redButtonStyle: React.CSSProperties = {
  background: '#DC3F48',
  boxShadow: 'inset 1px 2px 0px rgba(252, 255, 164, 0.25), inset 0px -5px 0px #282E44',
  borderRadius: '2px',
}

interface BetPanelProps {
  onBet: (amount: number) => void
}

export default function BetPanel({ onBet }: BetPanelProps) {
  const { betAmount, setBetAmount, lastBetAmount, balance, status } = useGameStore()
  const { presets, minBet, maxBet: sessionMaxBet } = useSessionStore()
  const { playSfx } = useAudioContext()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [draft, setDraft] = useState<string | null>(null)
  const draftRef = useRef<string | null>(null)
  const canBetAtPointerDownRef = useRef(false)
  const holdDelayRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const holdIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const maxBet = Math.max(0, Math.min(balance, sessionMaxBet, GAME_CONFIG.MAX_BET))
  // Calculator presets from session (first 3 values)
  const preset1 = presets[0] || 1
  const preset2 = presets[1] || 2
  const preset3 = presets[2] || 5
  
  const isPlaying = status === 'playing' || status === 'hot_rolls'
  const displayedAmount = draft ?? fromMinor(betAmount)
  const pendingBet = draft === null ? betAmount : parseBetInput(draft)
  const isBetInRange = pendingBet >= minBet && pendingBet <= maxBet
  const canBet = !isPlaying && pendingBet > 0 && isBetInRange
  const canRepeat = !isPlaying && lastBetAmount > 0 && lastBetAmount >= minBet && lastBetAmount <= maxBet

  const clampStepBet = useCallback((amount: number) => (
    Math.min(Math.max(amount, minBet), maxBet)
  ), [maxBet, minBet])

  const updateDraft = useCallback((value: string | null) => {
    draftRef.current = value
    setDraft(value)
  }, [])

  const commitInput = useCallback(() => {
    const value = draftRef.current
    updateDraft(null)
    if (value === null || isPlaying) return useGameStore.getState().betAmount

    const parsed = parseBetInput(value)
    const amount = parsed > 0 ? Math.min(parsed, maxBet) : 0
    setBetAmount(amount)
    return amount
  }, [isPlaying, maxBet, setBetAmount, updateDraft])

  const handleInputChange = (value: string) => {
    if (/^\d*(?:[.,]\d{0,2})?$/.test(value)) updateDraft(value)
  }

  const clearStepHold = useCallback(() => {
    if (holdDelayRef.current) {
      clearTimeout(holdDelayRef.current)
      holdDelayRef.current = null
    }
    if (holdIntervalRef.current) {
      clearInterval(holdIntervalRef.current)
      holdIntervalRef.current = null
    }
  }, [])

  const changeBetByStep = useCallback((direction: -1 | 1, withSound = true) => {
    if (withSound) playSfx('click')
    const current = commitInput()
    const baseAmount = current > 0 ? current : 0
    setBetAmount(clampStepBet(baseAmount + direction * BET_STEP_MINOR))
  }, [clampStepBet, commitInput, playSfx, setBetAmount])

  const startStepHold = useCallback((direction: -1 | 1) => (event: React.PointerEvent<HTMLButtonElement>) => {
    if (isPlaying) return
    event.preventDefault()
    inputRef.current?.blur()
    event.currentTarget.setPointerCapture?.(event.pointerId)
    clearStepHold()
    changeBetByStep(direction)
    holdDelayRef.current = setTimeout(() => {
      holdIntervalRef.current = setInterval(() => changeBetByStep(direction, false), 90)
    }, 350)
  }, [changeBetByStep, clearStepHold, isPlaying])

  useEffect(() => clearStepHold, [clearStepHold])

  useEffect(() => {
    if (isPlaying) {
      clearStepHold()
      updateDraft(null)
    }
  }, [clearStepHold, isPlaying, updateDraft])

  const handleAdd = (amount: number) => { playSfx('click'); setBetAmount(clampStepBet(commitInput() + amount)) }
  const handleClear = () => { playSfx('click'); updateDraft(null); setBetAmount(0) }
  const handleRepeat = () => { playSfx('click'); updateDraft(null); if (canRepeat) setBetAmount(lastBetAmount) }
  const handleBet = (event: React.MouseEvent<HTMLButtonElement>) => {
    // Blur may normalize an invalid draft before the same pointer click reaches BET.
    if (!canBet || (event.detail > 0 && !canBetAtPointerDownRef.current)) return
    const amount = commitInput()
    playSfx('click')
    onBet(amount)
  }

  // Стиль кнопки калькулятора (бордер #282E44)
  const calcBtnStyle: React.CSSProperties = {
    border: '1px solid #282E44',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }

  return (
    <div 
      className="w-full"
      style={{
        background: '#F8DDCA',
        borderRadius: '3px',
        padding: '3px',
      }}
    >
      <div 
        className="relative"
        onPointerDownCapture={() => { canBetAtPointerDownRef.current = canBet }}
        style={{ height: '118px' }}
      >
        {/* === КАЛЬКУЛЯТОР (левая часть, ~65% ширины) === */}
        <div 
          className="absolute flex flex-col gap-[2px]"
          style={{ left: '1.5%', right: '33%', top: '0', bottom: '0' }}
        >
          {/* Ряд 1: [-] [сумма] [+] — растягивается по высоте */}
          <div 
            className="flex items-center"
            style={{
              ...calcBtnStyle,
              flex: '1 1 0',
              padding: '0 6px',
              gap: '4px',
            }}
          >
            {/* Кнопка минус — тёмный символ, без тёмного фона */}
            <button 
              aria-label="Decrease bet"
              onPointerDown={startStepHold(-1)}
              onClick={(event) => { if (event.detail === 0) changeBetByStep(-1) }}
              onPointerUp={clearStepHold}
              onPointerLeave={clearStepHold}
              onPointerCancel={clearStepHold}
              onLostPointerCapture={clearStepHold}
              onContextMenu={(event) => event.preventDefault()}
              disabled={isPlaying}
              className="active:scale-90 transition-transform flex-shrink-0 w-[28px] h-[28px] flex items-center justify-center"
            >
              <div style={{ width: '14px', height: '3px', background: '#282E44', borderRadius: '1px' }} />
            </button>
            
            <input
              ref={inputRef}
              type="text"
              inputMode="decimal"
              enterKeyHint="done"
              aria-label="Bet amount"
              aria-invalid={pendingBet > 0 && !isBetInRange}
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              maxLength={12}
              value={displayedAmount}
              disabled={isPlaying}
              onFocus={(event) => {
                updateDraft(fromMinor(betAmount))
                event.currentTarget.select()
              }}
              onChange={(event) => handleInputChange(event.currentTarget.value)}
              onBlur={commitInput}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === 'Escape') {
                  event.preventDefault()
                  if (event.key === 'Escape') updateDraft(null)
                  event.currentTarget.blur()
                }
              }}
              className="min-w-0 w-full h-full flex-1 rounded-none border-0 bg-transparent p-0 outline-none focus:shadow-[inset_0_-2px_0_#282E44] disabled:opacity-100"
              style={{
                ...calcFontStyle,
                fontSize: displayedAmount.length > 10 ? '16px' : displayedAmount.length > 8 ? '20px' : '24px',
                userSelect: 'text',
                WebkitUserSelect: 'text',
                WebkitTouchCallout: 'default',
              }}
            />
            
            {/* Кнопка плюс — тёмный символ, без тёмного фона */}
            <button 
              aria-label="Increase bet"
              onPointerDown={startStepHold(1)}
              onClick={(event) => { if (event.detail === 0) changeBetByStep(1) }}
              onPointerUp={clearStepHold}
              onPointerLeave={clearStepHold}
              onPointerCancel={clearStepHold}
              onLostPointerCapture={clearStepHold}
              onContextMenu={(event) => event.preventDefault()}
              disabled={isPlaying}
              className="active:scale-90 transition-transform flex-shrink-0 w-[28px] h-[28px] flex items-center justify-center"
            >
              <div style={{ position: 'relative', width: '14px', height: '14px' }}>
                <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '14px', height: '3px', background: '#282E44', borderRadius: '1px' }} />
                <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '3px', height: '14px', background: '#282E44', borderRadius: '1px' }} />
              </div>
            </button>
          </div>

          {/* Ряд 2: preset buttons — растягивается по высоте */}
          <div className="flex gap-[2px]" style={{ flex: '1 1 0' }}>
            {[preset1, preset2, preset3].map((amt) => (
              <button
                key={amt}
                onClick={() => handleAdd(amt)}
                disabled={isPlaying}
                className="flex-1 active:scale-95 transition-transform"
                style={calcBtnStyle}
              >
                <span style={calcFontStyle}>{fromMinor(amt)}</span>
              </button>
            ))}
          </div>

          {/* Ряд 3: [×] [↻] — растягивается по высоте */}
          <div className="flex gap-[2px]" style={{ flex: '1 1 0' }}>
            <button
              onClick={handleClear}
              aria-label="Clear bet"
              disabled={isPlaying}
              className="flex-1 active:scale-95 transition-transform"
              style={calcBtnStyle}
            >
              <span style={{ ...calcFontStyle, fontSize: '30px', fontWeight: 900 }}>×</span>
            </button>
            <button
              onClick={handleRepeat}
              aria-label="Repeat bet"
              disabled={!canRepeat}
              className="flex-1 active:scale-95 transition-transform disabled:opacity-50"
              style={calcBtnStyle}
            >
              <span style={{ ...calcFontStyle, fontSize: '26px', fontWeight: 900, display: 'inline-block', transform: 'scaleX(-1) rotate(45deg)' }}>↻</span>
            </button>
          </div>
        </div>

        {/* === КНОПКА BET IN (правая часть, ~27% ширины) === */}
        <button
          onClick={handleBet}
          disabled={!canBet}
          className="absolute active:scale-95 transition-transform disabled:opacity-50 flex items-center justify-center"
          style={{
            ...redButtonStyle,
            left: '69%',
            right: '1.5%',
            top: '0',
            bottom: '0',
          }}
        >
          <span style={betInFontStyle}>
            BET<br/>in
          </span>
        </button>
      </div>
    </div>
  )
}
